import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const user = await register(name, email, password);
      toast.success(`Welcome, ${user.name}! Your account has been created.`);
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/8cb06352-fd18-4514-9e3b-904961bc2f5f/images/d8429818e078eae1d74078ad481dc946f534508be7b88fa493e610b33df8441d.png)`,
        }}
      >
        <div className="h-full w-full bg-[#002FA7]/5 backdrop-blur-[1px]"></div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Header */}
          <div className="mb-12">
            <p className="data-label mb-2">Get Started</p>
            <h1 className="text-4xl font-bold text-[#111111] tracking-tight">
              Create Account
            </h1>
            <p className="mt-4 text-[#555555]">
              Start trading with $1,000 paper balance.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="data-label block mb-2">Full Name</label>
              <Input
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono"
              />
            </div>

            <div>
              <label className="data-label block mb-2">Email</label>
              <Input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono"
              />
            </div>

            <div>
              <label className="data-label block mb-2">Password</label>
              <div className="relative">
                <Input
                  data-testid="register-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#111111]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="data-label block mb-2">Confirm Password</label>
              <Input
                data-testid="register-confirm-password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                required
                className="w-full h-12 border-[#111111]/10 rounded-none focus:border-[#002FA7] font-mono"
              />
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 btn-primary flex items-center justify-center gap-2 font-medium disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Create Account
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-[#555555]">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="login-link"
              className="text-[#002FA7] hover:underline font-medium"
            >
              Sign In
            </Link>
          </p>

          {/* Info */}
          <div className="mt-12 pt-8 border-t border-[#111111]/10">
            <p className="data-label mb-4">What You Get</p>
            <ul className="space-y-2 text-sm text-[#555555]">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#00C853]"></span>
                $1,000 Paper Trading Balance
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#00C853]"></span>
                10% Risk Per Trade Management
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#00C853]"></span>
                Circuit Breaker at $500
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-[#00C853]"></span>
                Email Notifications
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
